// components/media/SelectedEntity.js
import React, { useEffect, useState } from 'react';
import {
  Card,
  Typography,
  Badge,
  Button,
  Upload,
  Image,
  Modal,
  message,
  Space,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  PictureOutlined,
  AppstoreOutlined,
  FolderOutlined,
  FileOutlined,
  StarOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { 
  uploadMedia, 
  deleteMedia,
  getFeaturedImage,
  updateFeaturedImage,
  deleteFeaturedImage
} from '../../features/media/mediaSlice';

const { Title, Text } = Typography;

const SelectedEntity = ({ selectedEntity, onEditMedia, onRefresh }) => {
  const dispatch = useDispatch();
  const { 
    entityMedia, 
    uploading, 
    featuredImages, 
    featuredImageLoading, 
    featuredImageUploading,
    processing 
  } = useSelector((state) => state.media);

  const [featuredImageModal, setFeaturedImageModal] = useState(false);

  // Load featured image when entity changes
  useEffect(() => {
    if (selectedEntity) {
      dispatch(getFeaturedImage({
        entityType: selectedEntity.type,
        entityId: selectedEntity.id
      }));
    }
  }, [dispatch, selectedEntity]);

  // Get current featured image
  const getCurrentFeaturedImage = () => {
    if (!selectedEntity) return null;
    const key = `${selectedEntity.type}_${selectedEntity.id}`;
    const fromRedux = featuredImages[key];
    
    // If we have it in Redux, use that, otherwise use the entity's direct image
    if (fromRedux) return fromRedux;
    
    // Fallback to entity's direct image data
    if (selectedEntity.type === 'blog') {
      return selectedEntity.featuredImage || null;
    } else {
      return selectedEntity.coverImage ? {
        filename: selectedEntity.coverImage.split('/').pop(),
        url: selectedEntity.coverImage
      } : null;
    }
  };

  // Upload regular media files to specific entity
  const handleMediaUpload = async ({ file, onSuccess, onError }) => {
    if (!selectedEntity) {
      message.error('Please select a specific project or blog post first');
      onError('No entity selected');
      return;
    }

    const formData = new FormData();
    formData.append('files', file);
    formData.append('entityType', selectedEntity.type);
    formData.append('entityId', selectedEntity.id);

    try {
      await dispatch(uploadMedia(formData)).unwrap();
      onSuccess('ok');
      onRefresh();
    } catch (err) {
      onError('error');
    }
  };

  // Upload featured/cover image
  const handleFeaturedImageUpload = async ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      await dispatch(updateFeaturedImage({
        entityType: selectedEntity.type,
        entityId: selectedEntity.id,
        formData
      })).unwrap();
      message.success('Featured image updated successfully');
      setFeaturedImageModal(false);
      onSuccess('ok');
      onRefresh(); // Refresh to update the entity data
    } catch (err) {
      message.error('Failed to update featured image');
      onError('error');
    }
  };

  // Delete featured/cover image
  const handleFeaturedImageDelete = async () => {
    try {
      await dispatch(deleteFeaturedImage({
        entityType: selectedEntity.type,
        entityId: selectedEntity.id
      })).unwrap();
      message.success('Featured image deleted successfully');
      onRefresh(); // Refresh to update the entity data
    } catch (err) {
      message.error('Failed to delete featured image');
    }
  };

  // Delete regular media file
  const handleDeleteMedia = async (mediaId) => {
    try {
      await dispatch(deleteMedia({ id: mediaId, permanent: false })).unwrap();
      message.success('Media deleted successfully');
      onRefresh();
    } catch (err) {
      message.error('Failed to delete media');
    }
  };

  // Get selected entity media
  const getSelectedEntityMedia = () => {
    if (!selectedEntity) return [];
    const key = `${selectedEntity.type}_${selectedEntity.id}`;
    return entityMedia[key] || [];
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getMediaTypeIcon = (mediaType) => {
    switch (mediaType) {
      case 'image':
        return <PictureOutlined />;
      case 'video':
        return <FileOutlined />;
      case 'document':
        return <FileOutlined />;
      default:
        return <FileOutlined />;
    }
  };

  const getEntityTypeIcon = (type) => {
    switch (type) {
      case 'desproject':
        return <AppstoreOutlined style={{ color: '#1890ff' }} />;
      case 'devproject':
        return <FolderOutlined style={{ color: '#52c41a' }} />;
      case 'blog':
        return <EditOutlined style={{ color: '#fa8c16' }} />;
      default:
        return <FileOutlined />;
    }
  };

  if (!selectedEntity) {
    return (
      <Card>
        <div className="text-center py-12">
          <Title level={4} className="text-gray-500">
            No Entity Selected
          </Title>
          <Text className="text-gray-400">
            Please select a project or blog post to manage its media
          </Text>
        </div>
      </Card>
    );
  }

  const currentFeaturedImage = getCurrentFeaturedImage();

  return (
    <div>
      {/* Selected Entity Info */}
      <Card className="mb-6">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            {currentFeaturedImage ? (
              <div style={{ position: 'relative' }}>
                <Image
                  width={100}
                  height={100}
                  src={currentFeaturedImage.url}
                  alt={selectedEntity.name}
                  style={{ objectFit: 'cover', borderRadius: '8px' }}
                />
                <StarOutlined 
                  style={{ 
                    position: 'absolute', 
                    top: 4, 
                    right: 4, 
                    color: '#faad14',
                    background: 'white',
                    borderRadius: '50%',
                    padding: '2px',
                    fontSize: '14px'
                  }} 
                  title="Featured Image"
                />
              </div>
            ) : (
              <div className="w-25 h-25 bg-gray-100 rounded-lg flex items-center justify-center">
                {getEntityTypeIcon(selectedEntity.type)}
              </div>
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              {getEntityTypeIcon(selectedEntity.type)}
              <Title level={3} className="m-0">
                {selectedEntity.name}
              </Title>
              <Badge color="blue" text={selectedEntity.type} />
            </div>
            <Text className="text-gray-600 block mb-4">
              {selectedEntity.description}
            </Text>
            
            {/* Featured Image Management */}
            <div className="mb-4">
              <Text strong className="block mb-2">
                {selectedEntity.type === 'blog' ? 'Featured Image' : 'Cover Image'}:
              </Text>
              <Space>
                <Button
                  icon={<StarOutlined />}
                  onClick={() => setFeaturedImageModal(true)}
                  loading={featuredImageUploading}
                >
                  {currentFeaturedImage ? 'Update' : 'Add'} {selectedEntity.type === 'blog' ? 'Featured' : 'Cover'} Image
                </Button>
                {currentFeaturedImage && (
                  <Button
                    icon={<DeleteOutlined />}
                    danger
                    onClick={() => {
                      Modal.confirm({
                        title: `Remove ${selectedEntity.type === 'blog' ? 'Featured' : 'Cover'} Image`,
                        content: 'Are you sure you want to remove this image?',
                        onOk: handleFeaturedImageDelete
                      });
                    }}
                    loading={processing}
                  >
                    Remove
                  </Button>
                )}
              </Space>
            </div>

            <Divider />
            
            {/* Regular Media Upload */}
            <div className="flex items-center space-x-4">
              <Upload
                multiple
                customRequest={handleMediaUpload}
                showUploadList={false}
                accept="image/*,video/*,.pdf,.doc,.docx"
              >
                <Button type="primary" icon={<PlusOutlined />} loading={uploading}>
                  Upload Additional Media
                </Button>
              </Upload>
              <Text type="secondary">
                {getSelectedEntityMedia().length} additional media files
              </Text>
            </div>
          </div>
        </div>
      </Card>

      {/* Entity Media Grid */}
      {getSelectedEntityMedia().length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {getSelectedEntityMedia().map((mediaItem) => (
            <Card
              key={mediaItem._id}
              hoverable
              cover={
                mediaItem.mediaType === 'image' ? (
                  <Image
                    src={mediaItem.url}
                    alt={mediaItem.altText}
                    style={{ height: 150, objectFit: 'cover' }}
                    preview={{
                      mask: (
                        <div className="text-white text-xs">
                          <EyeOutlined className="text-lg mb-1" />
                          <br />
                          Preview
                        </div>
                      )
                    }}
                  />
                ) : (
                  <div className="h-150 bg-gray-100 flex items-center justify-center">
                    {getMediaTypeIcon(mediaItem.mediaType)}
                  </div>
                )
              }
              actions={[
                <EditOutlined
                  key="edit"
                  onClick={() => onEditMedia(mediaItem)}
                />,
                <DeleteOutlined
                  key="delete"
                  onClick={() => {
                    Modal.confirm({
                      title: 'Delete Media',
                      content: 'Are you sure you want to delete this media file?',
                      onOk: () => handleDeleteMedia(mediaItem._id)
                    });
                  }}
                />
              ]}
              size="small"
            >
              <Card.Meta
                title={
                  <Text ellipsis style={{ fontSize: '12px' }}>
                    {mediaItem.originalName}
                  </Text>
                }
                description={
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    {formatFileSize(mediaItem.fileSize)}
                  </Text>
                }
              />
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <div className="text-center py-12">
            <PictureOutlined className="text-6xl text-gray-400 mb-4" />
            <Title level={4} className="text-gray-500 mb-2">
              No Additional Media Files
            </Title>
            <Text className="text-gray-400">
              Upload some additional media files to get started
            </Text>
          </div>
        </Card>
      )}

      {/* Featured Image Upload Modal */}
      <Modal
        title={`${currentFeaturedImage ? 'Update' : 'Add'} ${selectedEntity.type === 'blog' ? 'Featured' : 'Cover'} Image - ${selectedEntity.name}`}
        visible={featuredImageModal}
        onCancel={() => setFeaturedImageModal(false)}
        footer={null}
        width={600}
      >
        <div className="mb-4">
          {currentFeaturedImage && (
            <div className="mb-4">
              <Text strong>Current Image:</Text>
              <div className="mt-2">
                <Image
                  width={200}
                  src={currentFeaturedImage.url}
                  alt="Current featured image"
                  style={{ borderRadius: '8px' }}
                />
              </div>
            </div>
          )}
          
          <Upload.Dragger
            customRequest={handleFeaturedImageUpload}
            showUploadList={false}
            accept="image/*"
            loading={featuredImageUploading}
          >
            <p className="ant-upload-drag-icon">
              <UploadOutlined />
            </p>
            <p className="ant-upload-text">Click or drag image to upload</p>
            <p className="ant-upload-hint">
              Upload a new {selectedEntity.type === 'blog' ? 'featured' : 'cover'} image for this {selectedEntity.type === 'blog' ? 'blog post' : 'project'}
            </p>
          </Upload.Dragger>
        </div>
      </Modal>
    </div>
  );
};

export default SelectedEntity;